import React from 'react';
import { Segmented, DatePicker, TimePicker, Input, Button } from 'antd';
import { WifiOutlined } from '@ant-design/icons';
import moment from 'moment';
import { SingleUsePassCreationFragmentProps } from 'fragments/passcodes/fragments/single-use-pass-creation/interfaces';
import { useSingleUsePassCreationController } from 'fragments/passcodes/fragments/single-use-pass-creation/single-use-pass-creation.controller';
import { windowEndLabel } from 'fragments/passcodes/fragments/single-use-pass-creation/date-helpers';
import { LockPickerFragment } from 'fragments/passcodes/fragments/lock-picker/lock-picker.fragment';
import { SingleUsePassSuccess } from 'fragments/passcodes/fragments/single-use-pass-creation/single-use-pass-success';
import { useTranslator } from 'tools/view-hooks/translator-hook';
import 'fragments/passcodes/fragments/single-use-pass-creation/single-use-pass-creation.scss';

export const SingleUsePassCreationFragment: React.FC<SingleUsePassCreationFragmentProps> = (props) => {
    const { translate } = useTranslator();
    const useController = props.useController ?? useSingleUsePassCreationController;
    const c = useController();

    if (c.pickerOpen) {
        return (
            <div className={'sup-creation-picker-overlay'}>
                <LockPickerFragment onSelect={c.onLockPicked} onClose={c.closePicker} />
            </div>
        );
    }

    if (c.step === 1 && c.created) {
        return <SingleUsePassSuccess controller={c} />;
    }

    const endLabel =
        c.whenMode === 'scheduled' && c.scheduledDate && c.scheduledTime
            ? windowEndLabel(c.scheduledDate, c.scheduledTime)
            : '';

    return (
        <div className={'sup-creation'}>
            <div className={'sup-creation-title'}>{translate({ key: 'visitor-pass.title' })}</div>

            <div className={'sup-creation-label'}>
                {translate({ key: 'visitor-pass.lock-field' })}{' '}
                <span className={'sup-creation-required'}>{translate({ key: 'visitor-pass.required' })}</span>
            </div>
            <button
                className={`sup-creation-lockfield${c.selectedLock ? '' : ' sup-creation-lockfield--empty'}`}
                onClick={c.openPicker}
            >
                {c.selectedLock
                    ? `${c.selectedLock.site_name} · ${c.selectedLock.lock_name}`
                    : translate({ key: 'visitor-pass.lock-placeholder' })}
            </button>

            <div className={'sup-creation-label'}>{translate({ key: 'visitor-pass.when' })}</div>
            <Segmented
                block
                size={'large'}
                value={c.whenMode}
                onChange={(v) => c.setWhenMode(v as 'now' | 'scheduled')}
                options={[
                    { label: translate({ key: 'visitor-pass.when-now' }), value: 'now' },
                    { label: translate({ key: 'visitor-pass.when-scheduled' }), value: 'scheduled' },
                ]}
            />
            {c.whenMode === 'scheduled' && (
                <>
                    <DatePicker
                        style={{ width: '100%', marginTop: '0.75rem', borderRadius: 8, height: 48 }}
                        format="DD/MM/YYYY"
                        disabledDate={(cur) => !!cur && cur < moment().startOf('day')}
                        onChange={(m) => c.onPickedDate(m ? m.format('YYYY-MM-DD') : '')}
                    />
                    <TimePicker
                        style={{ width: '100%', marginTop: '0.5rem', borderRadius: 8, height: 48 }}
                        format="HH:00"
                        disabledTime={() => ({
                            disabledHours: () =>
                                c.scheduledDate === moment().format('YYYY-MM-DD')
                                    ? Array.from({ length: moment().hour() + 1 }, (_, i) => i)
                                    : [],
                        })}
                        onChange={(m) => c.onPickedTime(m ? m.format('HH:00:00') : '')}
                    />
                    {endLabel && (
                        <div className={'sup-creation-hint'}>
                            {translate({ key: 'visitor-pass.window-hint', extra: { end: endLabel } })}
                        </div>
                    )}
                </>
            )}

            <div className={'sup-creation-label'}>{translate({ key: 'visitor-pass.visitor' })}</div>
            <Input
                maxLength={45}
                size={'large'}
                style={{ width: '100%', borderRadius: 8 }}
                placeholder={translate({ key: 'visitor-pass.visitor-placeholder' })}
                value={c.label}
                onChange={(e) => c.setLabel(e.target.value)}
            />

            <div className={'sup-creation-cta'}>
                {c.hasNetworkError && (
                    <div className={'sup-creation-neterror'}>
                        <WifiOutlined aria-hidden />
                        <span>{translate({ key: 'visitor-pass.net-error' })}</span>
                    </div>
                )}
                <Button
                    type={'primary'}
                    className={'sup-creation-cta-btn'}
                    loading={c.isLoading}
                    disabled={!c.canSubmit}
                    onClick={c.submit}
                >
                    {c.isLoading
                        ? translate({ key: 'visitor-pass.generating' })
                        : translate({ key: 'visitor-pass.generate' })}
                </Button>
            </div>
        </div>
    );
};
